/* eslint-disable camelcase */
import axios from 'axios';
import APIs from '@services/APIs';

const BASE_URL = APIs.API_URL;

export default {
  getName,
  find,
  verify,
  canHandleCountry,
  mapRemoteList,
  format
};

function getName() {
  return 'experian';
}

function canHandleCountry() {
  return true;
}

async function find({ address, country }) {
  const source = axios.CancelToken.source();

  window.addEventListener('offline', () => {
    source.cancel('Operation canceled by the user.');
  });

  if (!address) {
    return [];
  }

  const payload = {
    address,
    country
  };

  try {
    const { data } = await axios.post(`${BASE_URL}/address/autocomplete/${getName()}`, payload, {
      cancelToken: source.token
    });

    if (!data.result || !data.result.suggestions) {
      throw new Error('No results');
    }

    return mapRemoteList(data);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function mapRemoteList(data) {
  return data.result.suggestions.map(({ text, global_address_key }) => ({
    text,
    globalAddressKey: global_address_key
  }));
}

async function verify({ address, idHash, country }) {
  const addressData = await validateExperian({ address, idHash, country });

  return {
    ...addressData,
    provider: getName()
  };
}

async function validateExperian({ address, idHash, country }) {
  const source = axios.CancelToken.source();

  window.addEventListener('offline', () => {
    source.cancel('Operation canceled by the user.');
  });

  const payload = {
    address,
    idHash,
    country
  };

  try {
    const { data } = await axios.post(`${BASE_URL}/address/validate/${getName()}`, payload, {
      cancelToken: source.token
    });

    if (data.result && data.result.address) {
      return format(data.result);
    }
    return {};
  } catch (error) {
    console.error('resolved error exception', error);
    return {};
  }
}

function getAddressObjectKeysSorted() {
  const defaultAddressConcatenationOrder = [
    'address_line_1',
    'address_line_2',
    'address_line_3',
    'locality',
    'region',
    'postal_code'
  ];

  return defaultAddressConcatenationOrder;
}

function format(addressData) {
  const { address, components } = addressData;

  let fullAddress = getAddressObjectKeysSorted()
    .flatMap((key) => {
      if (address[key]) {
        return address[key];
      }
      return [];
    })
    .join(' ');

  fullAddress = fullAddress.trim();

  const formattedAddress = { ...address, fullAddress, homeAddress: fullAddress };

  if ('building' in components) {
    const { building } = components;
    Object.keys(building).forEach((key) => {
      if (key.includes('building_')) {
        formattedAddress[key] = building[key];
      } else {
        formattedAddress[`building_${key}`] = building[key];
      }
    });
  }

  if ('sub_building' in components) {
    const { sub_building } = components;

    if ('door' in sub_building) {
      const { door } = sub_building;
      Object.keys(door).forEach((key) => {
        if (key === 'full_name') {
          formattedAddress.unit_door_full_number = door[key];
        } else {
          formattedAddress[`unit_door_${key}`] = door[key];
        }
      });
    }

    if ('floor' in sub_building) {
      const { floor } = sub_building;
      Object.keys(floor).forEach((key) => {
        if (key === 'full_name') {
          formattedAddress.unit_floor_full_number = floor[key];
        } else {
          formattedAddress[`unit_floor_${key}`] = floor[key];
        }
      });
    }

    if ('entrance' in sub_building) {
      const { entrance } = sub_building;
      Object.keys(entrance).forEach((key) => {
        if (key === 'full_name') {
          formattedAddress.unit_entrance_full_number = entrance[key];
        } else {
          formattedAddress[`unit_entrance_${key}`] = entrance[key];
        }
      });
    }
  }

  if ('street' in components) {
    const { street } = components;
    Object.keys(street).forEach((key) => {
      formattedAddress[`street_${key}`] = street[key];
    });
  }

  if ('locality' in components) {
    const { locality } = components;
    Object.keys(locality).forEach((key) => {
      formattedAddress[`locality_${key}_name`] = locality[key].name;
    });
  }

  formattedAddress.address_api = getName();
  return formattedAddress;
}
